import { alpha, Box, lighten, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import clsx from 'clsx';
import React, { useEffect } from 'react';
import { sameLowC } from '../helpers';
import { getDisplayTri } from '../utils/spotPresenceCalculations';
import { useTriState } from '../hooks/usePersistedStates';

const useStyles = makeStyles(theme => ({
  left: {
    left: 0,
  },
  right: {
    right: 0,
  },

  base: {
    position: 'absolute',
    top: '0',
    width: '50%',
    height: '100%',
    border: 'none',
    textTransform: 'none',
    whiteSpace: 'nowrap',
    fontSize: '0.75em',
    padding: theme.spacing(0),
    display: 'flex',
    justifyContent: 'center',
    transition: '250ms cubic-bezier(0.4, 0, 0.2, 1)',
  },

  occupied: {
    backgroundColor: alpha(theme.palette.primary.main, 0.25),
    color: lighten(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.75 : 0),
    boxShadow: 'none',
  },

  ownSpot: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
  },
  ownSpotPending: {
    backgroundColor: theme.palette.secondary.main,
  },

  suggestUnsubscribe: {
    backgroundColor: alpha(theme.palette.primary.main, 0.5),
  },

  suggestOtherHalf: {
    background: alpha(theme.palette.primary.fg, 0.2),
  },

  disabled: {
    backgroundColor: 'transparent',
    color: theme.palette.primary.fg,
    opacity: 0.6,
  },
  badgeTri: {
    width: 'fit-content',
    padding: '0 2px',
    textAlign: 'center',
    color: theme.palette.primary.fg,
    background: theme.palette.primary.bg,
    zIndex: '2',
    borderRadius: '6px',
    margin: '0 auto',
    lineHeight: '1.1em',
    position: 'relative',
    '&:before': {
      content: "''",
      position: 'absolute',
      top: '-1px',
      height: 'calc(100% + 2px)',
      background: 'inherit',
      zIndex: '-1',
      border: `1px solid ${alpha(theme.palette.primary.fg, 0.4)}`,
    },
  },
  badgeLeft: {
    '&:before': {
      borderTopLeftRadius: 'inherit',
      borderBottomLeftRadius: 'inherit',
      borderRightColor: 'transparent',
      right: 'calc(50% + 1px)',
      width: '50%',
    },
  },
  badgeRight: {
    '&:before': {
      borderTopRightRadius: 'inherit',
      borderBottomRightRadius: 'inherit',
      borderLeftColor: 'transparent',
      width: 'calc(50% + 1px)',
      left: '50%',
    },
  },
  badgePending: {
    background: theme.palette.secondary.main,
    color: theme.palette.primary.bg,
    border: theme.palette.secondary.main,
  },
  badgeShare: {
    alignSelf: 'center',
  },
}));

const SpotButtonHalfDay = ({
  presences,
  onConflict,
  disabled,
  position,
  isShared,
  isHover,
  suggestOtherHalf,
  borderColor,
}) => {
  const classes = useStyles();
  const [ownTri] = useTriState('');

  const [presence] = presences;

  const rest = React.useMemo(
    () => presences?.slice(1) || [],
    [presences],
  );

  const isConflict = Boolean(rest.length);
  const isOccupied = Boolean(presence);
  const isOwnSpot = presences.some(({ tri }) => sameLowC(ownTri, tri));

  useEffect(() => {
    if (isConflict && rest.some(({ tri }) => sameLowC(ownTri, tri))) {
      onConflict(
        isConflict,
        presences.find(({ tri: t }) => ownTri !== t).tri,
      );
    }
  }, [isConflict, onConflict, ownTri, presences, rest]);

  const triPresence = (!isConflict && presence?.tri)
    || (isConflict && getDisplayTri(presences, ownTri));

  return (
    <>
      {presence?.tri && !isShared && (
        <Typography
          variant="caption"
          className={clsx({
            [classes.badgeTri]: true,
            [classes.badgeLeft]: position === 'left',
            [classes.badgeRight]: position === 'right',
            [classes.badgePending]: presence?.fake,
            [`hl-${presence?.tri}`]: presence?.tri && !disabled,
          })}
          sx={{
            border: `1px solid ${borderColor}`,
          }}
        >
          {triPresence}
        </Typography>
      )}
      <Box
        className={clsx({
          [classes.left]: position === 'left',
          [classes.right]: position === 'right',
          [classes.base]: true,
          [classes.ownSpot]: isOwnSpot,
          [classes.ownSpotPending]: isOwnSpot && presence?.fake,
          [classes.suggestUnsubscribe]: isOwnSpot && isHover && !('fake' in presence),
          [classes.suggestOtherHalf]: isHover && suggestOtherHalf,
          [classes.occupied]: isOccupied && !isOwnSpot,
          [classes.disabled]: disabled,
          [`hl-${presence?.tri}`]: presence?.tri && !disabled,
        })}
      >
        {isShared && (
          <Typography variant="caption" className={classes.badgeShare} title="share">
            {`${Array.from(triPresence)[0]}`}
          </Typography>
        )}
      </Box>
    </>
  );
};

export default React.memo(SpotButtonHalfDay);
